import React, { useState, useEffect } from 'react';
import { RefreshCw, Send, Eye, Save, AlertTriangle, Shield } from 'lucide-react';
import { dashboardApi } from '../services/api';

/**
 * Campaign management — mirrors Instantly campaigns and controls pushes.
 *
 * The push button always runs a dry run first. A live push is a second,
 * separate click on a result you have already seen, because a browser button
 * that sends to thousands of people on one click is a bad button.
 */

function statusClass(label) {
  switch (label) {
    case 'active': return 'bg-green-100 text-green-800';
    case 'paused': return 'bg-yellow-100 text-yellow-800';
    case 'draft': return 'bg-gray-100 text-gray-800';
    case 'completed': return 'bg-blue-100 text-blue-800';
    default: return 'bg-red-100 text-red-800';
  }
}

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [testMode, setTestMode] = useState(true);
  const [liveError, setLiveError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [edits, setEdits] = useState({});
  const [preview, setPreview] = useState({});
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await dashboardApi.getCampaigns2();
      setCampaigns(data.campaigns || []);
      setTestMode(data.testMode);
      setLiveError(data.liveError || null);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sync = async () => {
    setBusy('sync');
    try { await dashboardApi.syncCampaigns(); await load(); }
    catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(null); }
  };

  const field = (c, name) => (edits[c.id]?.[name] ?? c[name] ?? '');
  const edit = (c, name, value) =>
    setEdits({ ...edits, [c.id]: { ...(edits[c.id] || {}), [name]: value } });

  const save = async (c) => {
    setBusy(c.id);
    try {
      await dashboardApi.updateCampaign(c.id, edits[c.id] || {});
      setEdits({ ...edits, [c.id]: undefined });
      await load();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(null); }
  };

  const checkEligible = async (c) => {
    setBusy(c.id);
    try {
      const { data } = await dashboardApi.campaignEligible(c.id);
      setPreview({ ...preview, [c.id]: { matches: data.matches } });
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(null); }
  };

  const dryRun = async (c) => {
    setBusy(c.id);
    try {
      const { data } = await dashboardApi.pushCampaign(c.id, { dry_run: true, limit: 50 });
      setPreview({ ...preview, [c.id]: data });
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(null); }
  };

  const livePush = async (c) => {
    const n = preview[c.id]?.would_push;
    if (!window.confirm(
      `Push ${n} lead(s) into "${c.name}"?\n\n` +
      (testMode
        ? 'TEST MODE is on — every recipient will be rewritten to a seed inbox.'
        : 'TEST MODE IS OFF — these go to REAL influencers.'))) return;
    setBusy(c.id);
    try {
      const { data } = await dashboardApi.pushCampaign(c.id, { dry_run: false, limit: 50 });
      setPreview({ ...preview, [c.id]: { ...data, done: true } });
      await load();
    } catch (err) { setError(err.response?.data?.error || err.message); }
    finally { setBusy(null); }
  };

  if (loading) return <div className="p-6 text-gray-500">Loading campaigns…</div>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
          <p className="text-sm text-gray-500">
            Mirrored from Instantly. Targeting set here overrides the global filters.
          </p>
        </div>
        <button onClick={sync} disabled={busy === 'sync'}
          className="flex items-center px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
          <RefreshCw className="w-4 h-4 mr-2" /> Sync from Instantly
        </button>
      </div>

      <div className={`mb-4 p-3 rounded-lg text-sm flex items-center ${
        testMode ? 'bg-blue-50 border border-blue-200 text-blue-800'
                 : 'bg-red-50 border border-red-200 text-red-800'}`}>
        <Shield className="w-4 h-4 mr-2 flex-shrink-0" />
        {testMode
          ? 'TEST MODE is on — every push is redirected to your seed inboxes. Turning it off is a server-side change.'
          : 'TEST MODE IS OFF — pushes reach real influencers.'}
      </div>

      {liveError && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800 flex items-start">
          <AlertTriangle className="w-4 h-4 mr-2 mt-0.5" />
          Live status unavailable ({liveError}). Showing stored data.
        </div>
      )}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      {campaigns.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          No campaigns yet — hit “Sync from Instantly”.
        </div>
      )}

      <div className="space-y-4">
        {campaigns.map((c) => (
          <div key={c.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-start justify-between">
              <div>
                <h3 className="font-medium text-gray-900">{c.name}</h3>
                <p className="text-xs text-gray-400 font-mono">{c.instantly_campaign_id}</p>
              </div>
              <div className="flex items-center space-x-2">
                {c.is_test === 1 && (
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">test</span>
                )}
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass(c.instantly_status_label)}`}>
                  {c.instantly_status_label}
                </span>
              </div>
            </div>

            <div className="px-6 py-3 grid grid-cols-3 sm:grid-cols-6 gap-3 bg-gray-50 border-b border-gray-200 text-center">
              {[['pushed','Pushed'],['replied','Replied'],['interested','Interested'],
                ['booked','Booked'],['bounced','Bounced'],['unsubscribed','Unsub']].map(([k,label]) => (
                <div key={k}>
                  <p className="text-lg font-semibold text-gray-900">{c.stats[k]}</p>
                  <p className="text-xs text-gray-500">{label}</p>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
              {[['target_country','Countries','AU,DE'],
                ['target_min_followers','Min followers','100000'],
                ['target_max_followers','Max followers','5000000'],
                ['target_niches','Niches','any']].map(([name,label,ph]) => (
                <div key={name}>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
                  <input className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                    placeholder={`inherit (${ph})`} value={field(c, name)}
                    onChange={(e) => edit(c, name, e.target.value)} />
                </div>
              ))}
            </div>

            {preview[c.id] && (
              <div className="px-6 pb-3">
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm">
                  {preview[c.id].done ? (
                    <p className="text-green-700">
                      Pushed {preview[c.id].pushed}, failed {preview[c.id].failed}.
                    </p>
                  ) : preview[c.id].would_push !== undefined ? (
                    <>
                      <p className="font-medium text-gray-800 mb-1">
                        Dry run — {preview[c.id].would_push} lead(s) would be pushed:
                      </p>
                      <div className="max-h-40 overflow-y-auto font-mono text-xs text-gray-600">
                        {(preview[c.id].leads || []).slice(0, 20).map(l => (
                          <div key={l.username}>
                            @{l.username} · {l.email} · {Number(l.followers).toLocaleString()} · {l.country} · {l.niche || 'no niche'}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-gray-700">{preview[c.id].matches} influencer(s) match these filters.</p>
                  )}
                </div>
              </div>
            )}

            <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex flex-wrap gap-2">
              <button onClick={() => save(c)} disabled={busy === c.id || !edits[c.id]}
                className="flex items-center px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40">
                <Save className="w-4 h-4 mr-1.5" /> Save targeting
              </button>
              <button onClick={() => checkEligible(c)} disabled={busy === c.id}
                className="flex items-center px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                <Eye className="w-4 h-4 mr-1.5" /> How many match?
              </button>
              <button onClick={() => dryRun(c)} disabled={busy === c.id}
                className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                <Eye className="w-4 h-4 mr-1.5" /> Dry run
              </button>
              <button onClick={() => livePush(c)}
                disabled={busy === c.id || preview[c.id]?.would_push === undefined || preview[c.id]?.done}
                title={preview[c.id]?.would_push === undefined ? 'Run a dry run first' : ''}
                className="flex items-center px-3 py-1.5 text-sm bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-40">
                <Send className="w-4 h-4 mr-1.5" /> Push these leads
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Campaigns;
